// packages/chatbot/src/components/ExportUsagePanel.tsx

import React, { useMemo, useState } from 'react';
import { Card, Button, SelectField } from '@dopomogai/ui';
import { Copy, Check, Code2 } from 'lucide-react';
import type { ChatbotProps } from '../types';

// Remove undefined, null, functions, and some non-portable keys
function sanitizeConfig(input: ChatbotProps): Record<string, any> {
  const omitKeys = new Set<string>([
    'onCommand',
    'headlessTools',
    'headlessContext',
    'showDebugPanel',
  ]);

  const seen = new WeakMap<object, boolean>();
  const walk = (val: any): any => {
    if (val === undefined || val === null) return undefined;
    if (typeof val === 'function') return undefined;
    if (typeof val !== 'object') return val;
    if (seen.has(val)) return undefined;
    seen.set(val, true);
    if (Array.isArray(val)) {
      const arr = val.map(walk).filter((v) => v !== undefined);
      return arr;
    }
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(val)) {
      if (omitKeys.has(k)) continue;
      const next = walk(v);
      if (next !== undefined) out[k] = next;
    }
    return out;
  };

  const result = walk(input) || {};
  return result;
}

function jsonToObjectLiteral(json: string): string {
  return json;
}

type Variant = 'inline' | 'launcher';
type ImportStyle = 'bare' | 'detailed';
type Lang = 'tsx' | 'jsx';

function selectWidgetIdentifier(config: ChatbotProps['widgetInit']) {
  if (!config) return {};
  if (config.widgetId) return { widgetId: config.widgetId };
  if (config.templateId) return { templateId: config.templateId };
  return {};
}

export const ExportUsagePanel: React.FC<{ config: ChatbotProps }> = ({ config }) => {
  const [variant, setVariant] = useState<Variant>('inline');
  const [importStyle, setImportStyle] = useState<ImportStyle>('bare');
  const [lang, setLang] = useState<Lang>('tsx');
  const [copied, setCopied] = useState(false);

  const sanitized = useMemo(() => sanitizeConfig(config), [config]);

  const objectLiteral = useMemo(() => {
    const pruned = { ...sanitized };
    if (variant === 'launcher') {
      delete pruned.renderMode;
      delete pruned.position;
      delete pruned.isResizable;
    }
    const json = JSON.stringify(pruned, null, 2);
    return jsonToObjectLiteral(json);
  }, [sanitized, variant]);

  const widgetIdentifiers = useMemo(
    () => selectWidgetIdentifier(config.widgetInit),
    [config.widgetInit]
  );

  const code = useMemo(() => {
    const usingToolRegistry = Boolean(config.toolRegistry);
    const showImports = importStyle === 'detailed';
    const needsType = lang === 'tsx';

    const identifierLines: string[] = [];
    if (widgetIdentifiers.widgetId) {
      identifierLines.push(`widgetId: '${widgetIdentifiers.widgetId}',`);
    } else if (widgetIdentifiers.templateId) {
      identifierLines.push(`templateId: '${widgetIdentifiers.templateId}',`);
    }

    const widgetInitBlock = [
      'widgetInit: {',
      ...identifierLines.map((line) => `      ${line}`),
      config.widgetInit?.apiBaseUrlOverride
        ? `      apiBaseUrlOverride: '${config.widgetInit.apiBaseUrlOverride}',`
        : undefined,
      '    },',
    ]
      .filter(Boolean)
      .join('\n');

    // Common helper snippet for FE actions
    const feActionsImports = `import { createFrontendActions } from '@dopomogai/chatbot/tools/createFrontendActions';`;
    const feActionsSetup = [
      '// Optional: hook up on-page actions for the tool "frontend_actions".',
      'const myFrontendContext = {',
      "  selectPreset: (id: string) => console.log('selectPreset', id),",
      "  setFeature: (id: string, enabled: boolean) => console.log('setFeature', id, enabled),",
      "  openSection: (id: string) => console.log('openSection', id),",
      "  safeNavigate: (path: string) => console.log('navigate', path),",
      "  setPersonalizedCopy: (field: string, value: string, index?: number) => console.log('setCopy', { field, value, index }),",
      "  openQuoteWithCurrentConfig: async () => ({ url: '/quote/mock' }),",
      "  submitLeadEmail: async (email: string) => ({ ok: true, lead_id: 'lead-123' }),",
      "  fillFormField: (formId: string, fieldId: string, value: string) => console.log('fillFormField', { formId, fieldId, value }),",
      '};',
      'const headlessTools = createFrontendActions(myFrontendContext);',
    ].join('\n');

    if (variant === 'inline') {
      const imports = [
        "import { Chatbot } from '@dopomogai/chatbot';",
        feActionsImports,
        ...(showImports
          ? [
              "import { configureApiClient } from '@dopomogai/api-client';",
              "import { initSupabase } from '@dopomogai/supabase-client';",
            ]
          : []),
        usingToolRegistry ? "import { interactiveToolRegistry } from '../tools/toolRegistry';" : undefined,
        needsType ? "import type { ChatbotProps } from '@dopomogai/chatbot'; // optional" : undefined,
      ]
        .filter(Boolean)
        .join('\n');

      const cfgName = 'chatbotConfig';
      const cfgDecl =
        lang === 'tsx'
          ? `const ${cfgName}: ChatbotProps = ${objectLiteral};`
          : `const ${cfgName} = ${objectLiteral};`;

      const registryAssign = usingToolRegistry
        ? `  toolRegistry: interactiveToolRegistry,`
        : '';

      const patchedDecl = cfgDecl.replace(/^\{/, `{\n${registryAssign}`);

      return [
        imports,
        '',
        showImports
          ? [
              'configureApiClient({',
              `  apiBaseUrl: '${config.widgetInit?.apiBaseUrlOverride || 'https://your-api.example.com'}',`,
              '  supabaseClient: initSupabase(',
              "    'https://your-supabase-url',",
              "    'your-supabase-anon-key',",
              '  ),',
              '});',
              '',
            ].join('\n')
          : '',
        feActionsSetup,
        '',
        patchedDecl,
        '',
        'export default function MyPage() {',
        '  return (',
        "    <div style={{ width: '100%', height: '640px' }}>",
        `      <Chatbot`,
        `        authMode="widget"`,
        `        ${widgetInitBlock}`,
        `        headlessTools={headlessTools}`,
        `        {...${cfgName}}`,
        '      />',
        '    </div>',
        '  );',
        '}',
        '',
      ]
        .filter(Boolean)
        .join('\n');
    }

    const imports = [
      "import { Chatbot } from '@dopomogai/chatbot';",
      "import { GlobalChatProvider, SiteChatContainer } from '@dopomogai/chatbot/portal'; // adjust path if re-exported",
      feActionsImports,
      ...(showImports
        ? [
            "import { configureApiClient } from '@dopomogai/api-client';",
            "import { initSupabase } from '@dopomogai/supabase-client';",
          ]
        : []),
      usingToolRegistry ? "import { interactiveToolRegistry } from '../tools/toolRegistry';" : undefined,
      needsType ? "import type { ChatbotProps } from '@dopomogai/chatbot'; // optional" : undefined,
    ]
      .filter(Boolean)
      .join('\n');

    const cfgName = 'chatbotConfig';
    const cfgDecl =
      lang === 'tsx'
        ? `const ${cfgName}: ChatbotProps = ${objectLiteral};`
        : `const ${cfgName} = ${objectLiteral};`;

    const registryAssign = usingToolRegistry
      ? `  toolRegistry: interactiveToolRegistry,`
      : '';

    const patchedDecl = cfgDecl.replace(/^\{/, `{\n${registryAssign}`);

    return [
      imports,
      '',
      showImports
        ? [
            'configureApiClient({',
            `  apiBaseUrl: '${config.widgetInit?.apiBaseUrlOverride || 'https://your-api.example.com'}',`,
            '  supabaseClient: initSupabase(',
            "    'https://your-supabase-url',",
            "    'your-supabase-anon-key',",
            '  ),',
            '});',
            '',
          ].join('\n')
        : '',
      feActionsSetup,
      '',
      patchedDecl,
      '',
      'export default function MyPage() {',
      '  return (',
      '    <GlobalChatProvider>',
      `      <SiteChatContainer`,
      `        authMode="widget"`,
      `        ${widgetInitBlock}`,
      `        headlessTools={headlessTools}`,
      `        {...${cfgName}}`,
      '      />',
      '    </GlobalChatProvider>',
      '  );',
      '}',
      '',
      '// SiteChatContainer renders a floating launcher when closed and docks the Chatbot when opened.',
    ]
      .filter(Boolean)
      .join('\n');
  }, [config, widgetIdentifiers, objectLiteral, importStyle, lang, variant]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* ignore */
    }
  };

  return (
    <Card title="Export & Usage" icon={<Code2 />} className="mt-6">
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SelectField
            label="Variant"
            name="variant"
            value={variant}
            onChange={(v) => setVariant(v as Variant)}
            options={[
              { label: 'Inline <Chatbot />', value: 'inline' },
              { label: 'Launcher/Portal', value: 'launcher' },
            ]}
          />
          <SelectField
            label="Imports"
            name="imports"
            value={importStyle}
            onChange={(v) => setImportStyle(v as ImportStyle)}
            options={[
              { label: 'Bare essentials', value: 'bare' },
              { label: 'Show client init', value: 'detailed' },
            ]}
          />
          <SelectField
            label="Language"
            name="lang"
            value={lang}
            onChange={(v) => setLang(v as Lang)}
            options={[
              { label: 'TSX', value: 'tsx' },
              { label: 'JSX', value: 'jsx' },
            ]}
          />
        </div>

        <div className="flex items-center justify-between">
          <small className="text-slate-500">
            Headless tools and page-specific handlers aren’t serialized. Wire them on your page (see example).
          </small>
          <Button
            onClick={handleCopy}
            icon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            text={copied ? 'Copied' : 'Copy'}
            variant="primary"
          />
        </div>

        <pre className="bg-slate-900 text-slate-50 text-xs p-3 rounded-lg border-2 border-black overflow-auto max-h-[420px]">
{code}
        </pre>

        <div className="text-[11px] text-slate-500">
          • Chatbot props and render behaviour are documented in the component; SiteChatContainer portals the Chatbot and manages the floating launcher.<br />
          • If you rely on interactive tools, import and pass your tool registry just like on the Test Page.<br />
          • To enable FE actions, pass headlessTools={"{createFrontendActions(myFrontendContext)}"} to Chatbot or SiteChatContainer.<br />
          • Prefer <code>widgetId</code> (wid) when available; otherwise the legacy <code>templateId</code> is still accepted.
        </div>
      </div>
    </Card>
  );
};
